import { z } from 'zod';
import { utcInstantSchema } from '../content/common.js';
import { supportContractLimits } from './common.js';
export const sha256Schema = z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'Must be a lowercase hexadecimal SHA-256 digest');
const nullableRegistrationText = z
    .string()
    .trim()
    .min(1)
    .max(supportContractLimits.registrationText)
    .nullable();
/** The only registration fields an authenticated support workflow may receive. */
export const registrationSupportSummarySchema = z
    .object({
    legacySourceId: z.string().min(1).max(supportContractLimits.sourceId),
    name: nullableRegistrationText,
    email: z
        .string()
        .trim()
        .toLowerCase()
        .max(supportContractLimits.email)
        .pipe(z.email())
        .nullable(),
    phoneNumber: nullableRegistrationText,
    kycStatus: nullableRegistrationText,
    registeredAt: utcInstantSchema.nullable(),
    loyaltyCardNumber: nullableRegistrationText,
    whitehatUserId: nullableRegistrationText,
    whitehatPlayerId: nullableRegistrationText,
})
    .strict();
export const registrationSupportSummaryFields = Object.freeze(Object.keys(registrationSupportSummarySchema.shape));
/** Field-minimized PostgreSQL projection; no BSON or opaque KYC payload can enter it. */
export const registrationOperationalProjectionSchema = z
    .object({
    legacySourceId: z.string().min(1).max(supportContractLimits.sourceId),
    externalRegistrationId: nullableRegistrationText,
    startedAt: utcInstantSchema.nullable(),
    lastUpdatedAt: utcInstantSchema.nullable(),
    kycStatus: nullableRegistrationText,
    complete: z.boolean().nullable(),
    firstName: nullableRegistrationText,
    lastName: nullableRegistrationText,
    email: z
        .string()
        .trim()
        .toLowerCase()
        .max(supportContractLimits.email)
        .pipe(z.email())
        .nullable(),
    phoneNumber: nullableRegistrationText,
    loyaltyCardNumber: nullableRegistrationText,
    preferredLanguage: nullableRegistrationText,
    referralCode: nullableRegistrationText,
    registeredBy: nullableRegistrationText,
    whitehatUserId: nullableRegistrationText,
    whitehatPlayerId: nullableRegistrationText,
    sourceRecordSha256: sha256Schema,
    importedAt: utcInstantSchema,
    redactedAt: utcInstantSchema.nullable(),
})
    .strict();
export const registrationOperationalProjectionFields = Object.freeze(Object.keys(registrationOperationalProjectionSchema.shape));
/** Synthetic artifact metadata. A production/final marker is intentionally not representable. */
export const registrationExportManifestSchema = z
    .object({
    namespace: z.literal('registrations.registration'),
    exporterVersion: z.string().trim().min(1).max(50),
    synthetic: z.literal(true),
    recordCount: z.number().int().nonnegative(),
    byteCount: z.number().int().nonnegative(),
    sha256: sha256Schema,
    exportedAt: utcInstantSchema,
    earliestSourceAt: utcInstantSchema.nullable(),
    latestSourceAt: utcInstantSchema.nullable(),
})
    .strict();
export const registrationExportManifestFields = Object.freeze(Object.keys(registrationExportManifestSchema.shape));
export const registrationImportModes = ['dry_run', 'execute'];
export const registrationImportModeSchema = z.enum(registrationImportModes);
/** Explicit operator input; execute remains synthetic/non-production under this spec. */
export const registrationImportRequestSchema = z
    .object({
    mode: registrationImportModeSchema,
    synthetic: z.literal(true),
    expectedSha256: sha256Schema,
    expectedRecordCount: z.number().int().nonnegative(),
    runReference: z.string().trim().min(1).max(supportContractLimits.runReference),
})
    .strict();
export const registrationExcludedFieldCountSchema = z
    .object({
    field: z.string().trim().min(1).max(supportContractLimits.registrationText),
    count: z.number().int().nonnegative(),
})
    .strict();
export const registrationImportReconciliationSchema = z
    .object({
    mode: registrationImportModeSchema,
    synthetic: z.literal(true),
    exportSha256: sha256Schema,
    sourceCount: z.number().int().nonnegative(),
    acceptedCount: z.number().int().nonnegative(),
    rejectedCount: z.number().int().nonnegative(),
    insertedCount: z.number().int().nonnegative(),
    alreadyImportedCount: z.number().int().nonnegative(),
    excludedFields: z.array(registrationExcludedFieldCountSchema),
    runReference: z.string().trim().min(1).max(supportContractLimits.runReference),
    completedAt: utcInstantSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.acceptedCount + value.rejectedCount !== value.sourceCount) {
        context.addIssue({
            code: 'custom',
            path: ['sourceCount'],
            message: 'Accepted and rejected counts must reconcile to the source count',
        });
    }
    if (value.insertedCount + value.alreadyImportedCount !== value.acceptedCount) {
        context.addIssue({
            code: 'custom',
            path: ['acceptedCount'],
            message: 'Inserted and already-imported counts must reconcile to the accepted count',
        });
    }
});
//# sourceMappingURL=registration.js.map